import { map, take, mergeMap, switchMap, debounceTime } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ProductResp } from '@cemex-core/cmx-api/models/product/product-resp';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SALES_DOCUMENT_TYPE_ID } from '../../../../../shared/constants/sales-document-type-id';
import { SHIPMENT_LOCATION_TYPE_ID } from '../../../../../shared/constants/shipment-location-type-id';
import {
  ContractActions,
  ContractLoadAction,
  ContractLoadCompleteAction,
  LOAD_PRODUCT_CONTRACT,
  LOAD_CONTRACT_PAYMENTS,
  ContractsLoadPaymentsAction,
  ContractsLoadPaymentsCompleteAction,
  LOAD_PRODUCT_CONTRACT_OP,
} from '../../actions/contract.actions';
import { getContractRequestData } from '../../selectors/contract.selector';
import { GetProductContractsReq } from '@cemex-core/cmx-api/models/product/get-product-contracts-req';
import { CmxHttpClient } from '@cemex/cmx-client-layout';
import { getPaymentTermsRequestData } from '../../selectors/payment-terms.selector';
import { COUNTRY_CODE, PaymentTermService } from '@cemex-core/cmx-api';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

@Injectable()
export class ContractEffects {
  specificationConfigLoadComplete: Observable<ContractActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_PRODUCT_CONTRACT, LOAD_PRODUCT_CONTRACT_OP),
      debounceTime(500),
      mergeMap((action: ContractLoadAction) => {
        return this.store.select(getContractRequestData).pipe(
          take(1),
          map((data) => {
            return {
              ...data,
              productId: _safe(() => action.payload.product.productId, action.payload.productId),
              commercialDesc: _safe(() => action.payload.commercialDesc, action.payload['productDesc']),
              shipmentLocationId: data.jobsite.selected.shipmentLocationId,
              shipmentLocationPodId:
                data.pointOfDelivery &&
                data.pointOfDelivery.selected &&
                data.pointOfDelivery.selected.shipmentLocationId,
            };
          }),
        );
      }),
      mergeMap((data) => {
        // Add point of delivery to parameter if any
        let shipmentLocationId = `${data.shipmentLocationId}.${SHIPMENT_LOCATION_TYPE_ID.JOBSITE}`;
        if (data.shipmentLocationPodId) {
          shipmentLocationId = `${shipmentLocationId},${data.shipmentLocationPodId}.${SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY}`;
        }
        return this.getProductContracts({
          productId: String(data.productId),
          salesDocumentTypeId: String(SALES_DOCUMENT_TYPE_ID.CONTRACT),
          shipmentLocationId: `${shipmentLocationId}`,
          shippingConditionId: String(data.shippingCondition.shippingConditionId),
        }).pipe(
          take(1),
          map((response: ProductResp) => {
            const countryCode = _safe(() => data.shippingCondition.countryCode.trim(), undefined);
            // Filter out products that has different name only on UK
            const salesDocuments = response.products
              .filter((product) => {
                return countryCode !== COUNTRY_CODE.UNITED_KINGDOM || data.commercialDesc === product.commercialDesc;
              })
              .map((product) => {
                return product.salesDocument;
              });

            return new ContractLoadCompleteAction({
              productId: data.productId,
              salesDocuments,
            });
          }),
        );
      }),
    ),
  );

  loadContractPayments: Observable<ContractActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CONTRACT_PAYMENTS),
      debounceTime(500),
      switchMap((action: ContractsLoadPaymentsAction) => {
        const productId = _safe(() => action.payload.product.product.productId, action.payload.product.productId);
        const salesDocumentId = action.payload.contract.salesDocumentItemId;
        return this.store
          .select(getPaymentTermsRequestData)
          .pipe(take(1))
          .pipe(
            switchMap((data) => {
              return this.paymentTermsService.getCustomerPaymentTermsV2({
                countryCode: data.countryCode,
                productLineId: data.productLineId,
                jobsiteId: data.jobsiteId,
                customerId: data.customerId,
                productId: Number(productId),
                contractCode: action.payload.contract.salesDocumentCode,
              });
            }),
            map((response: any) => {
              return new ContractsLoadPaymentsCompleteAction({
                productId,
                salesDocumentId,
                paymentTerms: response.paymentTerms,
              });
            }),
          );
      }),
    ),
  );

  /**
   * Temp fix for api service
   * Cemex service is transforming the strings given into ASCII values
   * So for example: '715823.2,632277.3' is becoming '715823.2%2C632277.3'
   * @param request
   */
  private getProductContracts(request: GetProductContractsReq): Observable<ProductResp> {
    return this.http.get<ProductResp>(
      `/v2/mm/myproducts?productId=${request.productId}&salesDocumentTypeId=${request.salesDocumentTypeId}
      &shippingConditionId=${request.shippingConditionId}&shipmentLocationId=${request.shipmentLocationId}`,
    );
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private http: CmxHttpClient,
    private paymentTermsService: PaymentTermService,
  ) {}
}
